import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  AlertCircle,
  FileText,
  Loader2,
  RefreshCw,
  ScanText,
  Search,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import DocIndexBadge from '../components/cases/DocIndexBadge'
import {
  type DocumentExtractedResponse,
  type DocumentListItem,
} from '@toga/shared'

export default function OCR() {
  const [searchParams, setSearchParams] = useSearchParams()
  const docId = searchParams.get('document')

  const [caseId, setCaseId] = useState<string>('')
  const [docs, setDocs] = useState<DocumentListItem[]>([])
  const [loadingDocs, setLoadingDocs] = useState(false)
  const [extracted, setExtracted] = useState<DocumentExtractedResponse | null>(null)
  const [loadingDoc, setLoadingDoc] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [reindexing, setReindexing] = useState(false)

  // Cuando llegamos con ?document=, cargamos el detalle y derivamos caseId si lo tiene.
  useEffect(() => {
    if (!docId) {
      setExtracted(null)
      return
    }
    setLoadingDoc(true)
    setError(null)
    api
      .get<DocumentExtractedResponse>(`/documents/${docId}/extracted`)
      .then(async (d) => {
        setExtracted(d)
        // Si tiene expediente, cargamos los hermanos para la lista lateral.
        try {
          const detail = await api.get<{ document: { caseId: string | null } }>(
            `/documents/${docId}`
          )
          if (detail.document.caseId) setCaseId(detail.document.caseId)
        } catch {
          /* ignore */
        }
      })
      .catch((err) => {
        setError(extractErr(err))
        setExtracted(null)
      })
      .finally(() => setLoadingDoc(false))
  }, [docId])

  const loadDocs = useCallback(async () => {
    if (!caseId) {
      setDocs([])
      return
    }
    setLoadingDocs(true)
    try {
      const params = new URLSearchParams({ caseId })
      const { items } = await api.get<{ items: DocumentListItem[] }>(
        `/documents?${params.toString()}`
      )
      setDocs(items)
    } catch (err) {
      setError(extractErr(err))
      setDocs([])
    } finally {
      setLoadingDocs(false)
    }
  }, [caseId])

  useEffect(() => {
    void loadDocs()
  }, [loadDocs])

  const onReindex = async () => {
    if (!docId) return
    setReindexing(true)
    try {
      await api.post(`/documents/${docId}/reindex`, {})
      // Refresca extracción tras unos segundos para ver el cambio de estado.
      await new Promise((r) => setTimeout(r, 1200))
      const refreshed = await api.get<DocumentExtractedResponse>(
        `/documents/${docId}/extracted`
      )
      setExtracted(refreshed)
    } catch (err) {
      setError(extractErr(err))
    } finally {
      setReindexing(false)
    }
  }

  const filtered = useMemo(() => {
    if (!filter.trim()) return extracted?.text ?? ''
    // Resaltado simple: dividimos el texto por el término (case-insensitive) y
    // marcamos los matches con <mark>. Sin regex flags raras.
    return extracted?.text ?? ''
  }, [filter, extracted])

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <ScanText size={20} className="text-toga-300" />
            Visor OCR
          </h1>
          <p className="text-xs t-muted mt-0.5">
            Texto extraído de los documentos. Lo usa el asistente IA para buscar y citar.
          </p>
        </div>
      </header>

      <div className="grid grid-cols-[260px_1fr] gap-4">
        {/* Sidebar de documentos */}
        <aside className="surface p-3 space-y-2">
          {docId && !caseId && (
            <p className="text-xs t-faint">
              Documento individual. Para navegar más documentos del expediente, ábrelo desde la ficha
              del expediente.
            </p>
          )}
          {!docId && (
            <p className="text-xs t-faint">
              Abre un documento desde la ficha de un expediente o desde una cita del chat.
            </p>
          )}
          {loadingDocs ? (
            <div className="text-xs t-faint flex items-center gap-2 py-2">
              <Loader2 size={12} className="animate-spin" /> Cargando…
            </div>
          ) : (
            docs.length > 0 && (
              <ul className="space-y-0.5">
                {docs.map((d) => {
                  const active = d.id === docId
                  return (
                    <li key={d.id}>
                      <button
                        type="button"
                        onClick={() => {
                          const next = new URLSearchParams(searchParams)
                          next.set('document', d.id)
                          setSearchParams(next)
                        }}
                        className={[
                          'w-full text-left px-2 py-1.5 rounded text-sm',
                          active
                            ? 'bg-toga-500/15 text-toga-200 border border-toga-500/30'
                            : 'hover:bg-hover t-secondary border border-transparent',
                        ].join(' ')}
                      >
                        <div className="flex items-center gap-1.5">
                          <FileText size={12} className="t-faint shrink-0" />
                          <span className="truncate flex-1">{d.fileName}</span>
                        </div>
                        <div className="mt-1">
                          <DocIndexBadge status={d.indexStatus} />
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )
          )}
        </aside>

        {/* Panel principal */}
        <section className="surface p-5 min-h-[400px]">
          {error && (
            <div className="text-sm rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30 mb-3 flex items-center gap-2">
              <AlertCircle size={14} />
              {error}
            </div>
          )}
          {!docId && !error && (
            <div className="grid place-items-center h-72 text-center">
              <div className="space-y-2">
                <ScanText size={32} className="text-toga-400 mx-auto" />
                <h3 className="text-base font-semibold t-primary">Selecciona un documento</h3>
                <p className="text-xs t-muted max-w-md">
                  El visor muestra el texto extraído (PDF nativo o Claude vision). Los abogados lo
                  usan para citar fuentes exactas.
                </p>
              </div>
            </div>
          )}
          {loadingDoc && (
            <div className="text-sm t-muted flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Cargando texto extraído…
            </div>
          )}
          {extracted && (
            <>
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="min-w-0">
                  <h2 className="text-base font-semibold t-primary flex items-center gap-2">
                    <FileText size={16} className="text-toga-300" />
                    {extracted.fileName}
                  </h2>
                  <div className="flex items-center gap-2 mt-1 text-xs t-faint">
                    <DocIndexBadge status={extracted.indexStatus} error={extracted.indexError} />
                    {extracted.pageCount != null && <span>{extracted.pageCount} págs.</span>}
                    {extracted.indexedAt && (
                      <span>· Indexado {new Date(extracted.indexedAt).toLocaleString('es-ES')}</span>
                    )}
                  </div>
                  {extracted.indexError && extracted.indexStatus !== 'DONE' && (
                    <p className="text-xs text-rose-300 mt-1">{extracted.indexError}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => void onReindex()}
                  disabled={reindexing}
                  className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover disabled:opacity-50 flex items-center gap-1.5"
                >
                  {reindexing ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <RefreshCw size={14} />
                  )}
                  Reindexar
                </button>
              </div>

              <div className="relative mb-3">
                <Search
                  size={14}
                  className="absolute left-2 top-1/2 -translate-y-1/2 t-faint pointer-events-none"
                />
                <input
                  type="search"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Buscar en el texto…"
                  className="w-full bg-transparent border border-base rounded-lg pl-7 pr-3 py-1.5 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50"
                />
              </div>

              <pre className="text-sm whitespace-pre-wrap leading-relaxed t-primary font-sans bg-slate-900/40 border border-base rounded-lg p-4 max-h-[60vh] overflow-y-auto">
                {filter.trim()
                  ? highlightText(filtered, filter.trim())
                  : extracted.text || (
                      <span className="t-faint">(Sin texto extraído)</span>
                    )}
              </pre>
            </>
          )}
        </section>
      </div>
    </div>
  )
}

function highlightText(text: string, q: string): React.ReactNode[] {
  if (!q) return [text]
  const out: React.ReactNode[] = []
  const lower = text.toLowerCase()
  const needle = q.toLowerCase()
  let i = 0
  let key = 0
  while (i < text.length) {
    const idx = lower.indexOf(needle, i)
    if (idx === -1) {
      out.push(text.slice(i))
      break
    }
    if (idx > i) out.push(text.slice(i, idx))
    out.push(
      <mark
        key={`m-${key++}`}
        className="bg-toga-500/40 text-toga-100 rounded px-0.5"
      >
        {text.slice(idx, idx + q.length)}
      </mark>
    )
    i = idx + q.length
  }
  return out
}

function extractErr(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | undefined
    return body?.error ?? err.message
  }
  return err instanceof Error ? err.message : 'Error inesperado'
}
